import React, { useState } from 'react';
import { 
  Button, 
  Snackbar, 
  Alert, 
  CircularProgress,
  Box 
} from '@mui/material';
import { Share, Check, ContentCopy } from '@mui/icons-material';
import { encodeDocumentData, generateShareUrl, copyToClipboard } from '../utils/shareUtils';

export default function DocumentShareButton({ 
  documentTitle,
  documentType,
  contentLength,
  authors,
  reviewers,
  readers,
  writingTimeHours,
  reviewTimeHours,
  readingTimeHours,
  reviewCycles,
  finalCost,
  breakdown,
  variant = 'outlined',
  size = 'large',
  sx = {}
}) {
  const [isSharing, setIsSharing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  const handleShare = async () => {
    setIsSharing(true);
    
    try {
      // Encode the document data
      const encodedData = encodeDocumentData(
        documentTitle,
        documentType,
        contentLength,
        authors,
        reviewers,
        readers,
        writingTimeHours,
        reviewTimeHours,
        readingTimeHours,
        reviewCycles,
        finalCost,
        breakdown
      );
      
      if (!encodedData) {
        throw new Error('Failed to encode document data');
      }
      
      // Generate the share URL
      const shareUrl = generateShareUrl(encodedData);
      
      // Copy to clipboard
      const success = await copyToClipboard(shareUrl);
      
      if (success) {
        setShowSuccess(true);
      } else {
        throw new Error('Failed to copy to clipboard');
      }
    } catch (error) {
      console.error('Error sharing document:', error);
      setShowError(true);
    } finally {
      setIsSharing(false);
    }
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
  };

  const handleCloseError = () => {
    setShowError(false);
  };

  return (
    <Box>
      <Button
        variant={variant}
        size={size}
        onClick={handleShare}
        disabled={isSharing}
        startIcon={
          isSharing ? (
            <CircularProgress size={20} color="inherit" />
          ) : showSuccess ? (
            <Check />
          ) : (
            <Share />
          )
        }
        sx={{
          fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
          fontWeight: 700,
          borderRadius: 3,
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
          },
          '&:active': {
            transform: 'translateY(0px)',
          },
          ...sx,
        }}
      >
        {isSharing ? 'Generating Link...' : showSuccess ? 'Link Copied!' : 'Share Results'}
      </Button>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSuccess} 
          severity="success" 
          variant="filled"
          icon={<ContentCopy />}
          sx={{ 
            fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
            fontWeight: 600,
          }}
        >
          Document cost summary copied to clipboard! Share it with your team.
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseError} 
          severity="error" 
          variant="filled"
          sx={{ 
            fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
            fontWeight: 600,
          }}
        >
          Failed to create share link. Please try again.
        </Alert>
      </Snackbar>
    </Box>
  );
}